import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

export default function WhyChooseUs() {
  const { ref: topRef, isVisible: topVisible } = useScrollAnimation<HTMLDivElement>();
  const { ref: midRef, isVisible: midVisible } = useScrollAnimation<HTMLDivElement>();
  const { ref: bottomRef, isVisible: bottomVisible } = useScrollAnimation<HTMLDivElement>();

  return (
    <section className="bg-[#F5F0E8] py-24 px-6 lg:px-14">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-end mb-14">
          <div>
            <p className="overline mb-4">The ECS Difference</p>
            <h2 className="font-serif text-4xl lg:text-5xl font-light text-[#1A1409] leading-tight gold-line">
              Why Energy Contractors Choose ECS
            </h2>
          </div>
          <div>
            <p className="text-[#5A5A5A] text-base font-light leading-relaxed mb-7">
              Every energy contractor deserves a compliance partner who treats their business with the same seriousness they treat their own. That&rsquo;s exactly what you get with ECS.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2.5 bg-[#1A1409] text-[#F2EDE3] px-7 py-3.5 text-[11px] font-bold tracking-[0.2em] uppercase whitespace-nowrap cursor-pointer transition-colors duration-200 hover:bg-[#C9A030] hover:text-[#0A0A0A]"
            >
              Talk to an Expert
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-xs" />
              </span>
            </Link>
          </div>
        </div>

        {/* Top row: 2 large feature cards */}
        <div
          ref={topRef}
          className={`grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#1A1409]/08 mb-px fade-up ${topVisible ? 'visible' : ''}`}
        >
          {/* Feature card 1: Dark */}
          <div className="bg-[#0A0A0A] p-12 lg:p-14 flex flex-col justify-between min-h-72 group relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#C9A030]/[0.04] rounded-full translate-x-16 -translate-y-16 pointer-events-none" />
            <div>
              <div className="w-12 h-12 flex items-center justify-center mb-7">
                <i className="ri-shield-check-fill text-[#C9A030] text-3xl" />
              </div>
              <h3 className="font-serif text-3xl lg:text-4xl font-light text-[#F2EDE3] leading-snug mb-4">
                100% Approval Guaranteed
              </h3>
              <p className="text-[#9A9A9A] text-base font-light leading-relaxed max-w-sm">
                We don&rsquo;t stop until your ISNetworld, Veriforce, or Avetta account is fully approved and compliant. That&rsquo;s our commitment to every single client.
              </p>
            </div>
            <div className="w-10 h-0.5 bg-[#C9A030] mt-10" />
          </div>

          {/* Feature card 2: Gold */}
          <div className="bg-[#C9A030] p-12 lg:p-14 flex flex-col justify-between min-h-72 group relative overflow-hidden">
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-[#0A0A0A]/[0.06] rounded-full -translate-x-20 translate-y-20 pointer-events-none" />
            <div>
              <div className="w-12 h-12 flex items-center justify-center mb-7">
                <i className="ri-user-star-line text-[#0A0A0A] text-3xl" />
              </div>
              <h3 className="font-serif text-3xl lg:text-4xl font-light text-[#0A0A0A] leading-snug mb-4">
                Same Expert, Every Time
              </h3>
              <p className="text-[#0A0A0A]/70 text-base font-light leading-relaxed max-w-sm">
                You speak to the same dedicated person who knows your business — not a rotating call center. Personalized service built on genuine familiarity and trust.
              </p>
            </div>
            <div className="w-10 h-0.5 bg-[#0A0A0A]/40 mt-10" />
          </div>
        </div>

        {/* Middle row: 3 cards */}
        <div
          ref={midRef}
          className={`grid grid-cols-1 md:grid-cols-3 gap-px bg-[#1A1409]/08 mb-px fade-up ${midVisible ? 'visible' : ''}`}
        >
          <div
            className="bg-[#F5F0E8] p-9 hover:bg-white transition-colors duration-300 group border-t-0"
            style={{ transitionDelay: '0s' }}
          >
            <div className="w-10 h-10 flex items-center justify-center mb-5">
              <i className="ri-calendar-check-line text-[#C9A030] text-2xl" />
            </div>
            <h3 className="font-serif text-xl font-medium text-[#1A1409] mb-3 leading-snug">
              No Contracts Required
            </h3>
            <p className="text-[#5A5A5A] text-sm font-light leading-relaxed">
              Month-to-month service. No long-term commitments. No binding agreements. Cancel at any time — no questions asked.
            </p>
          </div>

          <div
            className="bg-[#111111] p-9 hover:bg-[#161616] transition-colors duration-300 group"
            style={{ transitionDelay: '0.07s' }}
          >
            <div className="w-10 h-10 flex items-center justify-center mb-5">
              <i className="ri-flashlight-fill text-[#C9A030] text-2xl" />
            </div>
            <h3 className="font-serif text-xl font-medium text-[#F2EDE3] mb-3 leading-snug">
              Quick Turnaround
            </h3>
            <p className="text-[#9A9A9A] text-sm font-light leading-relaxed">
              New account setups and score improvements handled with urgency. Compliance delays mean lost contracts — we treat your timeline as our own.
            </p>
          </div>

          <div
            className="bg-[#F5F0E8] p-9 hover:bg-white transition-colors duration-300 group"
            style={{ transitionDelay: '0.14s' }}
          >
            <div className="w-10 h-10 flex items-center justify-center mb-5">
              <i className="ri-global-line text-[#C9A030] text-2xl" />
            </div>
            <h3 className="font-serif text-xl font-medium text-[#1A1409] mb-3 leading-snug">
              Worldwide Coverage
            </h3>
            <p className="text-[#5A5A5A] text-sm font-light leading-relaxed">
              From Oklahoma City to Dubai, we serve energy contractors anywhere they work — across all 50 US states and internationally.
            </p>
          </div>
        </div>

        {/* Bottom row: Wide RAVS Plus card */}
        <div
          ref={bottomRef}
          className={`fade-up ${bottomVisible ? 'visible' : ''}`}
        >
          <div className="bg-[#1A1409] p-10 lg:p-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 relative overflow-hidden">
            <div className="absolute right-0 top-0 h-full w-64 opacity-[0.04]"
              style={{
                backgroundImage: 'url(https://public.readdy.ai/ai/img_res/66f0da899906675e2014a1f9c1c7dafc.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-shield-2-line text-[#C9A030] text-2xl" />
                </div>
                <h3 className="font-serif text-2xl font-medium text-[#F2EDE3] leading-snug">
                  RAVS Plus Specialists
                </h3>
              </div>
              <p className="text-[#9A9A9A] text-sm font-light leading-relaxed max-w-2xl">
                Deep expertise in one of the most demanding ISNetworld compliance requirements. RAVS Plus documentation is complex — we handle every detail so you meet even the toughest operator standards with confidence.
              </p>
            </div>
            <Link
              to="/services"
              className="relative z-10 flex-shrink-0 border border-[#C9A030]/40 text-[#C9A030] px-7 py-3 text-[11px] font-semibold tracking-[0.22em] uppercase whitespace-nowrap cursor-pointer transition-colors duration-200 hover:bg-[#C9A030] hover:text-[#0A0A0A] hover:border-[#C9A030]"
            >
              RAVS Plus Services
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}
